<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center mb-4 flex-wrap">
      <h1><v-icon color="primary" large>mdi-home-account</v-icon> Registrar Visita</h1>
    </div>

    <!-- Buscador principal (en la misma pantalla, no componente separado) -->
    <v-card rounded="xl" class="elevation-4 mb-4" :color="$vuetify.theme.dark ? 'grey darken-3' : 'white'">
      <v-card-text class="pa-4">
        <p class="text-subtitle-1 font-weight-bold mb-3">
          <v-icon color="primary" class="mr-1">mdi-magnify</v-icon>
          Buscar miembro por nombre o matrícula
        </p>
        <v-text-field
          v-model="busqueda"
          outlined rounded
          placeholder="Escribe el nombre o matrícula del miembro..."
          prepend-inner-icon="mdi-account-search"
          clearable
          autofocus
          @input="onBuscar"
          @keyup.enter="buscarPrimero"
          hide-details
        />

        <!-- Resultados de búsqueda -->
        <v-list v-if="resultados.length" class="mt-2 rounded-xl" dense>
          <v-list-item
            v-for="r in resultados" :key="r.id"
            @click="seleccionar(r)"
            class="rounded-lg mb-1"
            style="cursor:pointer;"
            :style="$vuetify.theme.dark ? 'background:rgba(255,255,255,0.05)' : 'background:rgba(23,6,255,0.04)'"
          >
            <v-list-item-avatar size="44">
              <img :src="urlImagen(r.imagen)" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">{{ r.nombre }}</v-list-item-title>
              <v-list-item-subtitle>{{ r.matricula }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-chip x-small :color="colorEstado(r.estado)" dark>{{ r.estado || 'SIN MEM.' }}</v-chip>
          </v-list-item>
        </v-list>
        <v-alert v-if="busqueda && busqueda.length >= 2 && !resultados.length && !buscando" type="info" dense text class="mt-2">
          No se encontraron miembros con ese nombre
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Card del miembro seleccionado -->
    <v-expand-transition>
      <div v-if="miembro">
        <v-card rounded="xl" class="elevation-8" :color="$vuetify.theme.dark ? 'grey darken-3' : 'white'">

          <!-- Banner de estado -->
          <div :class="['pa-3 d-flex align-center justify-center', bannerClass]">
            <v-icon large color="white" class="mr-2">{{ bannerIcon }}</v-icon>
            <span class="white--text text-h6 font-weight-bold">{{ bannerMensaje }}</span>
          </div>

          <v-row class="pa-4" no-gutters>
            <!-- Foto -->
            <v-col cols="12" sm="4" class="d-flex justify-center align-start pa-2">
              <v-avatar size="140" class="elevation-6">
                <img :src="urlImagen(miembro.imagen)" style="object-fit:cover;" />
              </v-avatar>
            </v-col>

            <!-- Datos -->
            <v-col cols="12" sm="8" class="pa-2">
              <div class="text-h5 font-weight-black mb-1">{{ miembro.nombre }}</div>
              <div class="text-caption grey--text mb-3">
                <v-icon x-small>mdi-card-account-details</v-icon>
                Matrícula: <strong>{{ miembro.matricula }}</strong>
              </div>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-list dense>
                    <v-list-item v-if="miembro.telefono">
                      <v-list-item-icon><v-icon small color="primary">mdi-phone</v-icon></v-list-item-icon>
                      <v-list-item-content><v-list-item-title>{{ miembro.telefono }}</v-list-item-title></v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="miembro.membresia">
                      <v-list-item-icon><v-icon small color="primary">mdi-wallet-membership</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ miembro.membresia }}</v-list-item-title>
                        <v-list-item-subtitle>Hasta: {{ miembro.fechaFin }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12" sm="6" class="d-flex align-center justify-center">
                  <!-- Días restantes -->
                  <div class="text-center" v-if="miembro.estado === 'ACTIVO'">
                    <div class="text-h3 font-weight-black" :class="diasRestantes <= 5 ? 'orange--text' : 'success--text'">
                      {{ diasRestantes }}
                    </div>
                    <div class="text-caption grey--text">días restantes</div>
                    <v-progress-circular
                      :value="pctMembresia" :color="diasRestantes <= 5 ? 'orange' : 'success'"
                      size="56" width="6" class="mt-2"
                    >
                      <span class="text-caption font-weight-bold">{{ pctMembresia }}%</span>
                    </v-progress-circular>
                  </div>
                  <div class="text-center" v-else>
                    <v-icon size="64" color="error">mdi-alert-circle</v-icon>
                    <div class="text-body-2 error--text font-weight-bold mt-1">
                      {{ miembro.estado === 'VENCIDO' ? 'Membresía vencida' : 'Sin membresía' }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Acciones -->
          <v-card-actions class="pa-4 pt-0 flex-wrap">
            <v-btn text @click="limpiar">
              <v-icon left>mdi-arrow-left</v-icon> Buscar otro
            </v-btn>
            <v-spacer />
            <!-- Renovar si está vencido -->
            <v-btn
              v-if="!miembro.membresia || miembro.estado === 'VENCIDO'"
              color="warning" rounded @click="abrirPago" class="mr-2"
            >
              <v-icon left>mdi-wallet-membership</v-icon> Renovar membresía
            </v-btn>
            <!-- Registrar visita si está activo -->
            <v-btn
              v-if="miembro.estado === 'ACTIVO'"
              color="success" x-large rounded
              :loading="cargando" @click="registrarVisita" elevation="4"
            >
              <v-icon left>mdi-check-bold</v-icon> Registrar visita
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </v-expand-transition>

    <!-- Dialog pago -->
    <v-dialog v-model="mostrarPago" persistent max-width="600">
      <realizar-pago
        v-if="miembro"
        :matricula="miembro.matricula"
        @cerrar="mostrarPago = false"
        @pagado="onPagado"
      />
    </v-dialog>

    <!-- Confirmación visita registrada -->
    <v-dialog v-model="mostrarConfirmacion" max-width="360">
      <v-card rounded="xl" class="text-center pa-4">
        <v-icon color="success" size="72">mdi-check-circle</v-icon>
        <v-card-title class="justify-center text-h6">¡Visita registrada!</v-card-title>
        <v-card-subtitle>{{ confirmacionNombre }}</v-card-subtitle>
        <v-card-actions>
          <v-btn block color="primary" rounded @click="mostrarConfirmacion = false; limpiar()">
            Registrar otra visita
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="mostrarMensaje" :color="mensaje.color" top :timeout="3000">
      {{ mensaje.texto }}
    </v-snackbar>
  </div>
</template>

<script>
import RealizarPago from '../Miembros/RealizarPago.vue'
import Utiles from '../../Servicios/Utiles'
import HttpService from '../../Servicios/HttpService'

export default {
  name: 'RegistrarVisita',
  components: { RealizarPago },

  data: function() {
    return {
      busqueda: '',
      resultados: [],
      buscando: false,
      busquedaTimer: null,
      miembro: null,
      cargando: false,
      mostrarPago: false,
      mostrarConfirmacion: false,
      confirmacionNombre: '',
      mostrarMensaje: false,
      mensaje: { texto: '', color: 'success' },
    }
  },

  computed: {
    diasRestantes: function() {
      if (!this.miembro || !this.miembro.fechaFin) return 0
      var hoy = new Date()
      var fin = new Date(this.miembro.fechaFin)
      var diff = Math.ceil((fin - hoy) / (1000 * 60 * 60 * 24))
      return diff > 0 ? diff : 0
    },
    pctMembresia: function() {
      if (!this.miembro || !this.miembro.fechaInicio || !this.miembro.fechaFin) return 0
      var inicio = new Date(this.miembro.fechaInicio)
      var fin = new Date(this.miembro.fechaFin)
      var hoy = new Date()
      var total = fin - inicio
      var transcurrido = hoy - inicio
      var pct = 100 - Math.round((transcurrido / total) * 100)
      return Math.max(0, Math.min(100, pct))
    },
    bannerClass: function() {
      if (this.miembro && this.miembro.estado === 'ACTIVO') return 'success'
      return 'error'
    },
    bannerIcon: function() {
      if (this.miembro && this.miembro.estado === 'ACTIVO') return 'mdi-shield-check'
      return 'mdi-shield-alert'
    },
    bannerMensaje: function() {
      if (!this.miembro) return ''
      if (this.miembro.estado === 'ACTIVO') return 'Membresía vigente — puede ingresar'
      if (this.miembro.estado === 'VENCIDO') return 'Membresía vencida — debe renovar'
      return 'Sin membresía activa'
    },
  },

  methods: {
    onBuscar: function() {
      clearTimeout(this.busquedaTimer)
      var self = this
      var t = this.busqueda
      if (!t || t.length < 2) { this.resultados = []; return }
      this.buscando = true
      this.busquedaTimer = setTimeout(function() {
        HttpService.buscar(t).then(function(data) {
          self.resultados = (data && data.miembros) ? data.miembros : []
          self.buscando = false
        })
      }, 300)
    },

    buscarPrimero: function() {
      if (this.resultados.length === 1) this.seleccionar(this.resultados[0])
    },

    seleccionar: function(m) {
      this.miembro = m
      this.resultados = []
      this.busqueda = ''
    },

    limpiar: function() {
      this.miembro = null
      this.busqueda = ''
      this.resultados = []
    },

    registrarVisita: function() {
      var self = this
      this.cargando = true
      var payload = {
        metodo: 'registrar',
        visita: {
          idMiembro: this.miembro.id,
          idMembresia: this.miembro.idMembresia,
          idUsuario: localStorage.getItem('idUsuario'),
        },
      }
      HttpService.registrar(payload, 'visitas.php').then(function(resultado) {
        self.cargando = false
        if (resultado) {
          self.confirmacionNombre = self.miembro.nombre
          self.mostrarConfirmacion = true
        }
      })
    },

    abrirPago: function() {
      this.mostrarPago = true
    },

    onPagado: function(ok) {
      if (ok) {
        this.mostrarPago = false
        this.mensaje = { texto: 'Membresía renovada. Ya puede registrar la visita.', color: 'success' }
        this.mostrarMensaje = true
        this.limpiar()
      }
    },

    urlImagen: function(img) { return Utiles.generarURL(img) },
    colorEstado: function(e) { return e === 'ACTIVO' ? 'success' : e === 'VENCIDO' ? 'error' : 'warning' },
  },
}
</script>


// WEBPACK FOOTER //
// src/components/Visitas/RegistrarVisita.vue